import { usePaginatedQuery } from "convex/react";
import { ClockIcon, DollarSignIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { ClioActivityItem } from "./types";

interface MatterActivitiesProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

function formatDuration(seconds: number | undefined): string {
	if (!seconds || seconds <= 0) return "—";
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	if (hours > 0) return `${hours}h ${minutes}m`;
	return `${minutes}m`;
}

function formatCurrency(n: number | undefined): string {
	if (n === undefined) return "—";
	return `$${n.toFixed(2)}`;
}

function isTimeEntry(item: ClioActivityItem): boolean {
	return item.type === "TimeEntry";
}

export function MatterActivities({
	connectionId,
	clioMatterId,
}: MatterActivitiesProps) {
	const pagination = usePaginatedQuery(
		api.clio.queries.listActivities,
		{ connectionId, clioMatterId },
		{ initialNumItems: 25 },
	);

	if (
		pagination.status === "LoadingFirstPage" &&
		pagination.results.length === 0
	) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading activities…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (pagination.results.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No activities on this matter.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{pagination.results.map((activity) => {
					const Icon = isTimeEntry(activity) ? ClockIcon : DollarSignIcon;
					return (
						<div key={activity._id} className="flex gap-3 p-4">
							<Icon className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
							<div className="flex min-w-0 flex-1 flex-col gap-1">
								<div className="flex items-baseline gap-2">
									<Badge variant="outline">{activity.type}</Badge>
									<span className="text-sm font-medium">
										{isTimeEntry(activity)
											? formatDuration(activity.quantityInSeconds)
											: formatCurrency(activity.total ?? activity.price)}
									</span>
									{activity.nonBillable && (
										<Badge variant="secondary" className="font-normal">
											Non-billable
										</Badge>
									)}
									{activity.billed && (
										<Badge variant="secondary" className="font-normal">
											Billed
										</Badge>
									)}
									<span className="ml-auto text-xs text-muted-foreground">
										{activity.date}
									</span>
								</div>
								{activity.note && (
									<div className="line-clamp-2 whitespace-pre-wrap text-sm text-foreground/80">
										{activity.note}
									</div>
								)}
								<div className="text-xs text-muted-foreground">
									{activity.userName ?? "—"}
								</div>
							</div>
						</div>
					);
				})}
				{pagination.status === "CanLoadMore" && (
					<div className="p-3">
						<Button
							variant="outline"
							size="sm"
							className="w-full"
							onClick={() => pagination.loadMore(25)}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}
